import { supabase } from "@/integrations/supabase/client";
import { invokeWithRetry } from './common/invokeWithRetry';
import i18n from "@/i18n";

// Feature flags for payments management edge function
const FEATURE_FLAGS = {
  GET_PAYMENTS: true,
  CREATE_PAYMENT: true,
  UPDATE_PAYMENT: true,
  DELETE_PAYMENT: true,
  GET_PAYMENT_SUMMARY: true,
  GET_PAYMENTS_WITH_SUBSCRIPTION: true,
  SEARCH_USER_BY_NAME: true,
};

export interface Payment {
  id: string;
  user_id: string;
  amount: number;
  payment_method: string;
  status: string;
  payment_date?: string;
  subscription_id?: string;
  booking_id?: string;
  description?: string;
  gym_id: string;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentInsert {
  user_id: string;
  amount: number;
  payment_method: string;
  status: string;
  payment_date?: string;
  subscription_id?: string;
  booking_id?: string;
  description?: string;
  gym_id: string;
}

export interface PaymentUpdate {
  amount?: number;
  payment_method?: string;
  status?: string;
  payment_date?: string;
  description?: string;
}

export interface GetPaymentsParams {
  gym_id: string;
  user_id?: string;
  subscription_id?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}

export interface PaymentSummaryRow {
  subscription_id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  subscription_type: string | null;
  start_date: string;
  end_date: string;
  subscription_price: number;
  subscription_status: string | null;
  payment_method: string | null;
  payment_frequency: string | null;
  is_paused: boolean | null;
  is_manually_activated: boolean | null;
  total_paid: number;
  payments_count: number;
  last_payment_date: string | null;
  coverage_state: string;
  coverage_pct: number;
  // Pending-aware fields
  pending_amount: number | null;
  pending_count: number | null;
  last_pending_date: string | null;
  total_including_pending: number | null;
  payments_count_including_pending: number | null;
  last_payment_date_including_pending: string | null;
  coverage_state_including_pending: string | null;
  coverage_pct_including_pending: number | null;
}

export interface PaymentWithSubscriptionRow {
  payment_id: string;
  payment_amount: number | null;
  payment_date: string | null;
  payment_status: string | null;
  payment_method: string | null;
  payment_description: string | null;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  subscription_id: string | null;
  subscription_price: number | null;
  subscription_type: string | null;
  subscription_status: string | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  payment_frequency: string | null;
  is_paused: boolean | null;
  is_manually_activated: boolean | null;
  gym_id: string | null;
}

/**
 * Get payments with filters
 */
export async function getPayments(params: GetPaymentsParams) {
  if (!FEATURE_FLAGS.GET_PAYMENTS) {
    // Fallback: direct database call
    let query = supabase
      .from("payments")
      .select("*")
      .eq("gym_id", params.gym_id)
      .order("payment_date", { ascending: false });

    if (params.user_id) query = query.eq("user_id", params.user_id);
    if (params.subscription_id) query = query.eq("subscription_id", params.subscription_id);
    if (params.status) query = query.eq("status", params.status);
    if (params.start_date) query = query.gte("payment_date", params.start_date);
    if (params.end_date) query = query.lte("payment_date", params.end_date);
    if (params.limit) query = query.limit(params.limit);

    const { data, error } = await query;
    if (error) return { data: null, error };
    return { data: data as Payment[], error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "get_payments",
    ...params,
  });

  if (authExpired) {
    return { data: [], error: null };
  }

  if (error) {
    console.error("[paymentsWrapper] get_payments error:", error);
    return { data: null, error };
  }

  return { data: data.payments as Payment[], error: null };
}

/**
 * Get payment summaries from v_subscription_payment_summary
 */
export async function getPaymentSummary(gymId: string, onlyActive: boolean = false) {
  if (!FEATURE_FLAGS.GET_PAYMENT_SUMMARY) {
    // Fallback: direct database call
    let query = supabase
      .from("v_subscription_payment_summary")
      .select("*")
      .eq("gym_id", gymId)
      .order("end_date", { ascending: false });

    if (onlyActive) {
      query = query.eq("subscription_status", "active");
    }

    const { data, error } = await query;
    if (error) return { data: null, error };
    return { data: data as PaymentSummaryRow[], error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "get_payment_summary",
    gym_id: gymId,
    only_active: onlyActive,
  });

  if (authExpired) {
    return { data: [], error: null };
  }

  if (error) {
    console.error("[paymentsWrapper] get_payment_summary error:", error);
    return { data: null, error };
  }

  return { data: data.summaries as PaymentSummaryRow[], error: null };
}

/**
 * Get payments with subscription details from v_payment_with_subscription
 */
export async function getPaymentsWithSubscription(gymId: string, subscriptionId?: string) {
  if (!FEATURE_FLAGS.GET_PAYMENTS_WITH_SUBSCRIPTION) {
    // Fallback: direct database call
    let query = supabase
      .from("v_payment_with_subscription")
      .select("*")
      .eq("gym_id", gymId)
      .order("payment_date", { ascending: false });

    if (subscriptionId) {
      query = query.eq("subscription_id", subscriptionId);
    }

    const { data, error } = await query;
    if (error) return { data: null, error };
    return { data: data as PaymentWithSubscriptionRow[], error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "get_payments_with_subscription",
    gym_id: gymId,
    subscription_id: subscriptionId,
  });

  if (authExpired) {
    return { data: [], error: null };
  }

  if (error) {
    console.error("[paymentsWrapper] get_payments_with_subscription error:", error);
    return { data: null, error };
  }

  return { data: data.payments as PaymentWithSubscriptionRow[], error: null };
}

export interface PaymentMethodDistributionResult {
  name: string;
  value: number;
  count: number;
}

/**
 * Normalise les variantes historiques de `payment_method` pour éviter deux
 * tranches identiques dans le camembert (ex. « transfer » et « bank_transfer »
 * désignent tous deux le virement bancaire).
 */
export function normalizePaymentMethod(rawMethod?: string | null): string {
  const method = (rawMethod || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!method) return "unknown";
  const aliases: Record<string, string> = {
    transfer: "bank_transfer",
    virement: "bank_transfer",
    virement_bancaire: "bank_transfer",
    bank: "bank_transfer",
    banktransfer: "bank_transfer",
    cheque: "check",
    espece: "cash",
    especes: "cash",
    carte: "card",
    credit_card: "card",
  };
  return aliases[method] ?? method;
}

/**
 * Aggregate completed payments by payment_method.
 * Returns distribution of amounts + count per method.
 */
export async function getPaymentMethodDistribution(
  gymId: string,
  status: string = 'completed'
): Promise<{ data: PaymentMethodDistributionResult[] | null; error: Error | null }> {
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "get_payments",
    gym_id: gymId,
    status,
  });

  if (authExpired) {
    return { data: [], error: null };
  }

  if (error) {
    console.error("[paymentsWrapper] getPaymentMethodDistribution error:", error);
    return { data: null, error };
  }

  const payments = (data.payments as Payment[]) || [];
  const byMethod: Record<string, { value: number; count: number }> = {};

  payments.forEach((payment) => {
    const method = normalizePaymentMethod(payment.payment_method);
    const amount = typeof payment.amount === 'number'
      ? payment.amount
      : parseFloat(payment.amount as unknown as string);
    if (!Number.isFinite(amount)) return;

    if (!byMethod[method]) {
      byMethod[method] = { value: 0, count: 0 };
    }
    byMethod[method].value += amount;
    byMethod[method].count += 1;
  });

  const result = Object.entries(byMethod)
    .map(([name, { value, count }]) => ({ name, value, count }))
    .sort((a, b) => b.value - a.value);

  return { data: result, error: null };
}

/**
 * Search user by name within a gym
 */
export async function searchUserByName(gymId: string, firstName: string, lastName: string) {
  if (!FEATURE_FLAGS.SEARCH_USER_BY_NAME) {
    // Fallback: direct database call (limited - doesn't filter by gym)
    const { data, error } = await supabase
      .from("user_profiles")
      .select("id")
      .eq("first_name", firstName)
      .eq("last_name", lastName)
      .single();

    if (error && error.code !== 'PGRST116') return { data: null, error };
    return { data: data?.id || null, error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "search_user_by_name",
    gym_id: gymId,
    first_name: firstName,
    last_name: lastName,
  });

  if (authExpired) {
    return { data: null, error: null };
  }

  if (error) {
    console.error("[paymentsWrapper] search_user_by_name error:", error);
    return { data: null, error };
  }

  return { data: data.user_id as string | null, error: null };
}

/**
 * Create a new payment
 */
export async function createPayment(payment: PaymentInsert) {
  if (!FEATURE_FLAGS.CREATE_PAYMENT) {
    // Fallback: direct database call
    const { data, error } = await supabase
      .from("payments")
      .insert({
        ...payment,
        payment_date: payment.payment_date || new Date().toISOString(),
      })
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data as Payment, error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "create_payment",
    ...payment,
  });

  if (authExpired) {
    // Session expirée : aucune écriture n'est partie. Renvoyer
    // `{ data: null, error: null }` faisait passer le `if (error)` des
    // appelants et annoncer un enregistrement inexistant.
    return { data: null, error: error ?? new Error(i18n.t("common:errors.sessionExpired")) };
  }

  if (error) {
    console.error("[paymentsWrapper] create_payment error:", error);
    return { data: null, error };
  }

  return { data: data.payment as Payment, error: null };
}

/**
 * Update an existing payment
 */
export async function updatePayment(
  payment_id: string,
  gym_id: string,
  updates: PaymentUpdate
) {
  if (!FEATURE_FLAGS.UPDATE_PAYMENT) {
    // Fallback: direct database call
    const { data, error } = await supabase
      .from("payments")
      .update(updates)
      .eq("id", payment_id)
      .eq("gym_id", gym_id)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data as Payment, error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "update_payment",
    payment_id,
    gym_id,
    updates,
  });

  if (authExpired) {
    // Session expirée : aucune écriture n'est partie. Renvoyer
    // `{ data: null, error: null }` faisait passer le `if (error)` des
    // appelants et annoncer un enregistrement inexistant.
    return { data: null, error: error ?? new Error(i18n.t("common:errors.sessionExpired")) };
  }

  if (error) {
    console.error("[paymentsWrapper] update_payment error:", error);
    return { data: null, error };
  }

  return { data: data.payment as Payment, error: null };
}

/**
 * Delete a payment
 */
export async function deletePayment(payment_id: string, gym_id: string) {
  if (!FEATURE_FLAGS.DELETE_PAYMENT) {
    // Fallback: direct database call
    const { error } = await supabase
      .from("payments")
      .delete()
      .eq("id", payment_id)
      .eq("gym_id", gym_id);

    return { data: error ? null : { success: true }, error };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("payments-management", {
    action: "delete_payment",
    payment_id,
    gym_id,
  });

  if (authExpired) {
    // Annoncer `{ success: true }` sur une session expirée faisait afficher
    // « Paiement supprimé » alors qu'aucune suppression n'était partie, et la
    // ligne réapparaissait au rechargement suivant. `invokeWithRetry` renseigne
    // désormais `error` en parallèle d'`authExpired` : on le propage.
    return { data: null, error: error ?? new Error(i18n.t("common:errors.sessionExpired")) };
  }

  if (error) {
    console.error("[paymentsWrapper] delete_payment error:", error);
    return { data: null, error };
  }

  return { data, error: null };
}
